import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Banner from "components/mis-survey/sections/banner/Banner";
import styles from "./survey.module.css";
import { IoIosArrowRoundForward } from "react-icons/io";
import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import preventionImage from "images/headerimges/mis-survey/prevention-banner.svg";
import Checkboxes from "components/ui/Checkboxes";
import {
	conditionalQuizzes as conditionalQuizzesFn,
	generateQuizSchema,
	transformUserResponse,
} from "utils/helpers";
import DropDownInput from "components/ui/DropDownInput";
import toast from "react-hot-toast";
import {
	useGetSectionById,
	useGetSectionQuestions,
	useInitiateSurveySection,
	useSubmitUATSurveyData,
} from "api/hooks";
import { misSurveySection } from "utils/constants";
import { useSurveySectionContent } from "context/survey-track/SurveySectionContext";
import DOBPicker from "components/mis-survey/YearPicker";
import CountryPicker from "components/mis-survey/CountryPicker";
import { useDispatch, useSelector } from "react-redux";
import { setRespondent, setResponses } from "store/features/misSurveySlice";
import RadioInputs from "components/ui/RadioInputBoxes";
import { Backdrop, CircularProgress } from "@mui/material";
import { secureStorage } from "utils/encryptionUtil";

let nextSectionId = null;
let historyStatus = true;
let sectionHistoryData = [];

const SurveyPage = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { responses, localRespondent } = useSelector(
		(state) => state.misSurvey
	);
	const [emptyOthersValue, setEmptyOthersValue] = useState(false);
	const [isDateInValid, setIsDateInValid] = useState(false);
	// const currentSection = searchParam.get("section");
	const [enabled, setEnabled] = useState(false);
	const [userDOB, setUserDOB] = useState(null);
	const [otherQuizText, setOtherQuizText] = useState({});

	const sessionRespondent =
		secureStorage.getSession("::dc::us-mls::resp") || "";

	const getQuestions = useGetSectionQuestions(nextSectionId);

	const initialConditionalQuizzes = conditionalQuizzesFn([]);

	const hiddenQuizzes = useRef(
		secureStorage.getSession("hidden:::keys") ?? initialConditionalQuizzes
	);

	const sessionSurveyCode = secureStorage.getSession("::dc::us-mls") || "";
	const sessionAgentNumber =
		secureStorage.getSession("::agent::us-mls") || "";
	const sessionChannel = secureStorage.getSession("::channel::us-mls") || "";

	useEffect(() => {
		sectionHistoryData = secureStorage.getLocal("sectionHistoryData") ?? [];
		nextSectionId = sectionHistoryData[0];
		sectionHistoryData.shift();
		if (localRespondent?.gender === "Male" && Number(nextSectionId) === 5) {
			let newMaleSections = sectionHistoryData.filter((id) => id !== 5);
			nextSectionId = newMaleSections.shift();
			sectionHistoryData = newMaleSections;
			secureStorage.setLocal("sectionHistoryData", newMaleSections);
		} else {
			secureStorage.setLocal("sectionHistoryData", sectionHistoryData);
			// nextSectionId = sectionHistoryData.shift();
		}
		historyStatus = false;
	}, []);

	const { isLoading: isInitializing, isSuccess } = useInitiateSurveySection(
		sessionSurveyCode,
		sessionRespondent?.phone_number,
		nextSectionId,
		1,
		enabled
	);

	const getCurrentSection = useGetSectionById(Number(nextSectionId));

	const isEndOfSurvey = Number(nextSectionId) === 8;

	const falseSection = Number(nextSectionId) > 8;

	const handlelNextSection = () => {
		if (
			localRespondent?.gender === "Male" &&
			(Number(nextSectionId) === 5 || Number(nextSectionId) === 4)
		) {
			sectionHistoryData = secureStorage.getLocal("sectionHistoryData");
			let newMaleSections = sectionHistoryData.filter((id) => id !== 5);
			nextSectionId = newMaleSections.shift();

			sectionHistoryData = newMaleSections;
			secureStorage.setLocal("sectionHistoryData", newMaleSections);
		} else {
			nextSectionId = sectionHistoryData?.shift();
			secureStorage.setLocal("sectionHistoryData", sectionHistoryData);
		}

		if (!isEndOfSurvey) {
			setEnabled(true);
		}

		secureStorage.setSession("hidden:::keys", []);

		// if (!isEndOfSurvey && Number(nextSectionId) >= 4) {
		// 	dispatchSection({
		// 		type: "UPDATE",
		// 		payload: {
		// 			currentSection: misSurveySection.MINI_REWARD,
		// 			showConfetti: false,
		// 			formCompletedAt: null,
		// 			rewardPrice: getRewardPrice(Number(nextSectionId)),
		// 			nextSectionUrl: `/ml-participant`,
		// 		},
		// 	});
		// }

		setTimeout(() => {
			if (isEndOfSurvey) {
				navigate("/ml-thank-you", { replace: true });
			}
			// else if (!isEndOfSurvey && Number(nextSectionId) >= 4) {
			// 	navigate("/ml-reward", { replace: true });
			// }
			setEnabled(false);
		}, 400);
	};

	const { submitUATSurvey, isLoading } = useSubmitUATSurveyData({
		onSuccess: () => {
			toast.success("survey submitted successfully");
		},
		onError: (error) => {
			const errorMessage = error?.response?.data?.message;
			if (Array.isArray(errorMessage) && errorMessage.length > 1) {
				toast.error("An error occured, please try again");
			} else {
				toast.error(
					errorMessage || "unable to submit survey, please try again"
				);
			}
		},
	});

	const formSchema = generateQuizSchema(
		getQuestions?.data?.data ?? [],
		hiddenQuizzes
	);

	const {
		control,
		handleSubmit,
		setValue,
		register,
		formState: { errors },
	} = useForm({
		resolver: zodResolver(formSchema),
		defaultValues: responses?.[nextSectionId],
	});

	const formData = useWatch({ control });

	useEffect(() => {
		dispatch(setResponses({ currentSection: nextSectionId, formData }));
		hiddenQuizzes.current.forEach((id) =>
			setValue(id.toString(), undefined)
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formData, nextSectionId]);

	const onSubmit = (data) => {
		hiddenQuizzes.current.forEach((id) =>
			setValue(id.toString(), undefined)
		);

		if (Number(nextSectionId) === 1) {
			dispatch(
				setRespondent({
					...localRespondent,
					gender: formData?.["1"] === "1" ? "Male" : "Female",
				})
			);
		}

		if (Number(nextSectionId) === 1) {
			transformUserResponse(data, otherQuizText, (responses) => {
				submitUATSurvey({
					survey_id: 1,
					channel: sessionChannel !== "" ? sessionChannel : "direct",
					agent:
						sessionAgentNumber !== ""
							? Number(sessionAgentNumber)
							: null,
					survey_code: sessionSurveyCode,
					section_id: Number(nextSectionId),
					respondent: {
						phone_number: sessionRespondent?.phone_number,
						age: userDOB?.year === "" ? 0 : userDOB?.age,
						year_of_birth: userDOB?.year,
						gender: data?.["1"] === "1" ? "Male" : "Female",
						longitude: localRespondent?.longitude,
						latitude: localRespondent?.latitude,
						state: localRespondent?.state,
						town: localRespondent?.town,
						lga: localRespondent?.lga,
					},
					response: responses,
					is_survey_end: isEndOfSurvey,
				});
			});
		} else {
			transformUserResponse(data, otherQuizText, (responses) => {
				submitUATSurvey({
					survey_id: 1,
					channel: sessionChannel !== "" ? sessionChannel : "direct",
					agent:
						sessionAgentNumber !== ""
							? Number(sessionAgentNumber)
							: null,
					survey_code: sessionSurveyCode,
					section_id: Number(nextSectionId),
					respondent: {
						phone_number: sessionRespondent?.phone_number,
						age: localRespondent?.age,
						year_of_birth: localRespondent?.year_of_birth,
						gender: localRespondent?.gender,
						longitude: localRespondent?.longitude,
						latitude: localRespondent?.latitude,
						state: localRespondent?.state,
						town: localRespondent?.town,
						lga: localRespondent?.lga,
					},
					response: responses,
					is_survey_end: isEndOfSurvey,
				});
			});
		}
		handlelNextSection();
	};

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: "smooth" });
	}, []);

	useEffect(() => {
		if (getQuestions?.data?.data && hiddenQuizzes.current?.length <= 0) {
			secureStorage.setSession(
				"hidden:::keys",
				initialConditionalQuizzes
			);
			hiddenQuizzes.current = initialConditionalQuizzes;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getQuestions?.data?.data]);

	const updateLocalRespondent = (age, year) => {
		setUserDOB({
			age,
			year,
		});
		dispatch(
			setRespondent({
				...localRespondent,
				phone_number: sessionRespondent?.phone_number,
				age: year === "" ? 0 : age,
				year_of_birth: year,
			})
		);
	};

	const updateStateLocation = (locationData) => {
		dispatch(
			setRespondent({
				...localRespondent,
				longitude: locationData?.longitude,
				latitude: locationData?.latitude,
				state: locationData?.userLocation?.state,
				town: locationData?.userLocation?.town,
				lga: locationData?.userLocation?.lga,
			})
		);
	};

	const updateHiddenQuizes = (logicObj) => {
		const allQuestions = getQuestions.data.data;
		const currentQuizIndex = allQuestions.findIndex(
			(q) => q.quiz_id === logicObj?.currentId
		);
		const nextIndex = allQuestions.findIndex(
			(q) => q.quiz_id === logicObj?.targetedId
		);

		const lastQuizId = allQuestions?.[allQuestions?.length - 1]?.quiz_id;
		const lastQuizIndex = allQuestions.findIndex(
			(q) => q.quiz_id === lastQuizId
		);

		const quizesInbetween = allQuestions
			.slice(
				currentQuizIndex + 1,
				nextIndex === -1 ? lastQuizIndex + 1 : nextIndex
			)
			.map((q) => q.quiz_id);

		//REMOVE action indicates adding the quizes in between that should be skipped
		if (logicObj?.action === "REMOVE") {
			const newHiddenQuizzes = hiddenQuizzes.current?.filter(
				(id) => id !== logicObj?.targetedId
			);

			const updatedHiddenQuizes = [
				...new Set([...newHiddenQuizzes, ...quizesInbetween]),
			];
			secureStorage.getSession("hidden:::keys", updatedHiddenQuizes);
			hiddenQuizzes.current = updatedHiddenQuizes;
		}

		//ADD action indicates removing the previous hidden quizes in between
		if (logicObj?.action === "ADD") {
			const newHiddenQuizzes = hiddenQuizzes.current?.filter(
				(id) => !quizesInbetween?.includes(id)
			);
			const nestedChildQuizIds = [];

			const targetedQuiz = getQuestions?.data?.data?.find(
				(quiz) => quiz.quiz_id === logicObj?.targetedId
			);

			targetedQuiz?.options?.forEach((option) => {
				if (option?.next_question_id) {
					nestedChildQuizIds.push(option.next_question_id);
				}
			});

			const updatedHiddenQuizes = [
				...new Set([...newHiddenQuizzes, ...nestedChildQuizIds]),
			];
			secureStorage.setSession("hidden:::keys", updatedHiddenQuizes);

			hiddenQuizzes.current = updatedHiddenQuizes;
		}
	};

	const isButtonDisabled =
		getQuestions.isLoading ||
		!getQuestions.data ||
		falseSection ||
		isLoading ||
		emptyOthersValue ||
		isDateInValid ||
		!nextSectionId;

	return (
		<section className={styles.container}>
			<div>
				<Banner
					title={
						getCurrentSection.data?.data?.[0]?.section_type ?? ""
					}
					description={""}
					imageURL={preventionImage}
				/>
			</div>

			<Backdrop
				sx={(theme) => ({
					color: "#fff",
					zIndex: theme.zIndex.drawer + 3,
				})}
				open={isInitializing && !isSuccess}
			>
				<CircularProgress color="inherit" />
				<p className="ml-5 font-light">Loading section</p>
			</Backdrop>

			<form
				onSubmit={handleSubmit(onSubmit)}
				data-testid="main_survey_form"
				key={nextSectionId}
			>
				{/* <div className={styles.form_earning}>
					<p>You Get back airtime</p>
					<p>when you complete this survey</p>
				</div> */}
				{getQuestions.isLoading && (
					<div className={styles.form_earning}>
						<p className={"!text-gray-500"}>
							Your section questions are loading, please wait....
						</p>
					</div>
				)}

				{getQuestions?.data?.data
					?.filter(
						(quiz) => !hiddenQuizzes.current.includes(quiz?.quiz_id)
					)
					.map((quiz, i) => {
						return (
							<React.Fragment key={i + 1}>
								{
									<>
										{quiz.quiz_type === "PLAIN_TEXT" &&
											quiz.form_element === "DATE" && (
												<Controller
													name={quiz.quiz_id.toString()}
													control={control}
													render={() => (
														<div
															className={
																styles.form_education
															}
														>
															<div className="flex mb-1 gap-2">
																<span className="text-center bg-primary text-white py-[2px] px-1 rounded-sm !text-sm mr-2 block h-6">
																	{i + 1}
																</span>
																<p>
																	In what year
																	were you
																	born?
																</p>
															</div>
															<DOBPicker
																onDOBChange={(
																	age,
																	year
																) => {
																	setValue(
																		quiz.quiz_id.toString(),
																		String(
																			year +
																				"#dob#"
																		),
																		{
																			shouldValidate: true,
																			shouldTouch: true,
																			shouldDirty: true,
																		}
																	);
																	updateLocalRespondent(
																		age,
																		year
																	);
																}}
																initialAge={
																	localRespondent?.age ??
																	null
																}
																initialYear={
																	localRespondent?.year_of_birth ??
																	null
																}
																hasInvalidDate={(
																	isInvalid
																) => {
																	setIsDateInValid(
																		isInvalid
																	);
																}}
															/>

															{Boolean(
																errors?.[
																	quiz.quiz_id
																]
															) && (
																<small className="text-danger">
																	{errors?.[
																		quiz
																			.quiz_id
																	]
																		? "this question is required"
																		: ""}
																</small>
															)}
															{isDateInValid && (
																<small className="text-danger">
																	please use
																	the calendar
																	icon to
																	select a
																	proper date
																</small>
															)}
														</div>
													)}
												/>
											)}

										{quiz.quiz_type === "PLAIN_TEXT" &&
											quiz.form_element ===
												"LOCATION" && (
												<Controller
													name={quiz.quiz_id.toString()}
													control={control}
													render={() => (
														<div
															className={
																styles.form_education
															}
														>
															<CountryPicker
																onLocationChange={(
																	locationData
																) => {
																	updateStateLocation(
																		locationData
																	);
																	setValue(
																		quiz.quiz_id.toString(),
																		locationData.latitude &&
																			locationData.longitude
																			? locationData.latitude.toString() +
																					"," +
																					locationData.longitude.toString()
																			: "",
																		{
																			shouldValidate: true,
																			shouldTouch: true,
																			shouldDirty: true,
																		}
																	);
																}}
																index={i}
															/>

															{Boolean(
																errors?.[
																	quiz.quiz_id
																]
															) && (
																<small className="text-danger">
																	{errors?.[
																		quiz
																			.quiz_id
																	]
																		? "this question is required"
																		: ""}
																</small>
															)}
														</div>
													)}
												/>
											)}

										{quiz.quiz_type === "SINGLE_SELECT" &&
											quiz.form_element === "RADIO" && (
												<Controller
													name={quiz.quiz_id.toString()}
													control={control}
													render={({ field }) => (
														<RadioInputs
															field={field}
															question={
																quiz.quiz_text
															}
															{...(register(
																quiz.quiz_id.toString()
															),
															{
																required: true,
															})}
															quizOptions={
																quiz.options
															}
															index={i}
															hasError={Boolean(
																errors[
																	quiz.quiz_id
																]
															)}
															errorMessage={
																errors?.[
																	quiz.quiz_id
																]
																	? "this question is required"
																	: ""
															}
															onLogicChange={(
																logic
															) => {
																updateHiddenQuizes(
																	logic
																);
															}}
														/>
													)}
												/>
											)}

										{quiz.quiz_type === "SINGLE_SELECT" &&
											quiz.form_element ===
												"DROP-DOWN" && (
												<Controller
													name={quiz.quiz_id.toString()}
													control={control}
													render={({ field }) => (
														<DropDownInput
															hideOthers={false}
															field={field}
															question={
																quiz.quiz_text
															}
															quizOptions={
																quiz.options
															}
															{...(register(
																quiz.quiz_id.toString()
															),
															{
																required: true,
															})}
															hasError={Boolean(
																errors[
																	quiz.quiz_id
																]
															)}
															errorMessage={
																errors?.[
																	quiz.quiz_id
																]
																	? "this question is required"
																	: ""
															}
															index={i}
															onLogicChange={(
																logic
															) => {
																updateHiddenQuizes(
																	logic
																);
															}}
															hasOthersError={(
																hasError
															) => {
																setEmptyOthersValue(
																	hasError
																);
															}}
															onOtherTextChange={({
																id,
																text,
															}) => {
																setOtherQuizText(
																	(
																		prevValue
																	) => {
																		return {
																			...prevValue,
																			[id]: {
																				id,
																				text,
																			},
																		};
																	}
																);
															}}
														/>
													)}
												/>
											)}

										{quiz.quiz_type === "MULTIPLE_SELECT" &&
											quiz.form_element ===
												"CHECKBOX" && (
												<div>
													<Controller
														name={quiz.quiz_id.toString()}
														control={control}
														render={({ field }) => (
															<Checkboxes
																field={field}
																setValue={
																	setValue
																}
																hasOthersError={(
																	hasError
																) => {
																	setEmptyOthersValue(
																		hasError
																	);
																}}
																index={i}
																question={
																	quiz.quiz_text
																}
																quizOptions={
																	quiz.options
																}
																{...(register(
																	quiz.quiz_id.toString()
																),
																{
																	required: true,
																})}
																hasError={Boolean(
																	errors[
																		quiz
																			.quiz_id
																	]
																)}
																errorMessage={
																	errors?.[
																		quiz
																			.quiz_id
																	]
																		? "select at least one option"
																		: ""
																}
																onLogicChange={(
																	logic
																) => {
																	updateHiddenQuizes(
																		logic
																	);
																}}
															/>
														)}
													/>
												</div>
											)}
									</>
								}
							</React.Fragment>
						);
					})}

				<div className="!flex !flex-wrap !items-center !gap-2">
					<button type="submit" disabled={isButtonDisabled}>
						{isLoading
							? "Submitting"
							: `${isEndOfSurvey ? "Submit" : "Next"}`}{" "}
						<IoIosArrowRoundForward color="white" size={24} />
					</button>
				</div>
			</form>
		</section>
	);
};

export default SurveyPage;
